import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { HydrateContent } from "@foundation/hydrate-model";
import {
  IntradayDataPoint,
  Periodicity,
  type ChartDateRange,
} from "@bloomberg/octagon-shared";
import type { TickerChartDateRange, TickerSeries } from "~types/types";
import {
  fetchIntradayChartData,
  fetchIntradayChartDataWithDates,
} from "~api/chart-data/fetchIntradayChartData";
import hydrateArray from "~transforms/hydrateArray";
import { tickerChart } from "~models/TickerChart";
import { fetchHistoryChartData } from "~api/chart-data/fetchHistoryChartData";
import {
  getChartConfig,
  getChartDates,
  getYTDInDays,
} from "~utils/ticker-chart";

const CHART_REFERENCE_FIELDS = [
  // issuedCurrency
  "DS004",
  // lastUpdate
  "UTIME",
  // longName
  "DS520",
  // marketStatus
  "PR286",
  // name
  "DS002",
  // previousClosingPriceOneTradingDayAgo
  "PR050",
];

const INTRADAY_OHLC_DATA_POINTS = [
  IntradayDataPoint.Open,
  IntradayDataPoint.High,
  IntradayDataPoint.Low,
  IntradayDataPoint.Close,
  IntradayDataPoint.Volume,
].join(",") as IntradayDataPoint;

const INTRADAY_VOLUME_DATA_POINTS = [
  IntradayDataPoint.Close,
  IntradayDataPoint.Volume,
].join(",") as IntradayDataPoint;

const HISTORY_OHLC_FIELDS = [
  "PX_OPEN",
  "PX_HIGH",
  "PX_LOW",
  "PX_LAST",
  "VOLUME",
].join(",");

function getIntradayDataPoints(
  isOhlcv: boolean,
  includeVolume: boolean
): IntradayDataPoint {
  if (isOhlcv) return INTRADAY_OHLC_DATA_POINTS;
  if (includeVolume) return INTRADAY_VOLUME_DATA_POINTS;

  return IntradayDataPoint.Close;
}

function getHistoryFields(isOhlcv: boolean, includeVolume: boolean): string {
  if (isOhlcv) return HISTORY_OHLC_FIELDS;
  if (includeVolume) return "PX_LAST,VOLUME";

  return "PX_LAST";
}

function fetchIntradayData(
  tickers: string[],
  chartDateRange: TickerChartDateRange,
  isOhlcv: boolean,
  includeVolume: boolean,
  intervalMinutes: number,
  tradingDayCount?: number
): Promise<unknown> {
  const referenceFields = CHART_REFERENCE_FIELDS.join(",");
  const dataPoints = getIntradayDataPoints(isOhlcv, includeVolume);

  if (tradingDayCount) {
    return fetchIntradayChartData(
      tickers,
      tradingDayCount,
      dataPoints,
      intervalMinutes,
      referenceFields
    );
  }

  const { startDate, endDate } = getChartDates(chartDateRange);

  return fetchIntradayChartDataWithDates(
    tickers,
    startDate,
    endDate,
    dataPoints,
    intervalMinutes,
    referenceFields
  );
}

function fetchHistoryData(
  tickers: string[],
  chartDateRange: ChartDateRange,
  isOhlcv: boolean,
  includeVolume: boolean,
  periodicity: Periodicity
): Promise<unknown> {
  const fields = getHistoryFields(isOhlcv, includeVolume);

  return fetchHistoryChartData(
    tickers,
    chartDateRange,
    fields,
    CHART_REFERENCE_FIELDS.join(","),
    periodicity
  );
}

async function fetchTickerChartData(
  tickers: string[],
  chartDateRange: TickerChartDateRange,
  isOhlcv: boolean,
  includeVolume: boolean
): Promise<TickerSeries[]> {
  const ytdDays = chartDateRange === "YTD" ? getYTDInDays() : 0;
  const config = getChartConfig(chartDateRange, isOhlcv, ytdDays);

  let chartData: unknown;

  if (config.source === "intraday") {
    chartData = await fetchIntradayData(
      tickers,
      chartDateRange,
      isOhlcv,
      includeVolume,
      config.intervalMinutes,
      config.tradingDayCount
    );
  } else {
    chartData = await fetchHistoryData(
      tickers,
      chartDateRange as ChartDateRange,
      isOhlcv,
      includeVolume,
      config.periodicity
    );
  }

  const { data: hydratedData, errors } = hydrateArray(
    chartData as HydrateContent,
    tickerChart
  );

  if (!hydratedData || errors.length > 0) {
    throw new Error(
      `Failed to hydrate ticker chart data for tickers: ${tickers.join(
        ", "
      )}, errors: ${errors?.join("; \n")}`
    );
  }

  const seriesData = hydratedData as TickerSeries[];

  // Sync output order to match the incoming tickers array
  const seriesById = new Map(seriesData.map((s) => [s.id ?? s.name, s]));

  const ordered: TickerSeries[] = [];

  for (const t of tickers) {
    const series = seriesById.get(t);

    if (series) {
      ordered.push(series);
      seriesById.delete(t);
    }
  }

  // Append any unmatched series at the end
  for (const remaining of seriesById.values()) {
    ordered.push(remaining);
  }

  return ordered;
}

const getTickerChartDataQueryKey = (
  tickers: string[],
  chartDateRange: TickerChartDateRange,
  isOhlcv: boolean,
  includeVolume: boolean
) => [
  "ticker-chart-data",
  chartDateRange,
  tickers.join(","),
  isOhlcv,
  includeVolume,
];

export function useTickerChartData(
  tickers: string[],
  chartDateRange: TickerChartDateRange,
  includeVolume = false,
  isOhlcv = false
) {
  const ytdDays = useMemo(
    () => (chartDateRange === "YTD" ? getYTDInDays() : 0),
    [chartDateRange]
  );

  const config = getChartConfig(chartDateRange, isOhlcv, ytdDays);

  const query = useQuery({
    queryKey: getTickerChartDataQueryKey(
      tickers,
      chartDateRange,
      isOhlcv,
      includeVolume
    ),
    queryFn: () =>
      fetchTickerChartData(tickers, chartDateRange, isOhlcv, includeVolume),
    enabled: tickers.length > 0,
    staleTime: config.staleTime,
  });

  return {
    ...query,
    dataInterval: config.dataInterval,
    staleTime: config.staleTime,
  };
}
